import type { CompanySummary } from "./CompanySummary";
import { EnumLocHeaderOption } from "./EnumLocHeaderOption";
import { EnumLocLabelType } from "./EnumLocLabelType";

export interface IOption {
  value: string;
  text: string;
}

export const MaxNumberOfSingleLocsForImmediateDownload = 1000;

// Regular options - InHouse, PrivateLabel, Repack
// Only hyperlink options - Transport, InternalWash, UpgradeWash,
// MaintenanceWash, StorageFacility, StorageTanks
export interface ILabelTypePrintOptions {
  isLinkOption?: boolean;
  generateLocRequired?: (
    selectedPrintOption: IPrintOption,
    productCount: number
  ) => boolean;
  letterHeaderOptions: ILocHeaderOption[];
  sortOrderOptions?: IOption[];
  printOptions?: IPrintOption[];
  letterHeaderPreference?: string;
  sortOrderPreference?: string;
  printOptionPreference?: string;
  nameBuilderPreference?: string;
}

export interface ILocHeaderOption extends IOption {
  validateRestrictions?: (company: CompanySummary) => boolean;
}

export interface IPrintOption extends IOption {
  showFolderName?: boolean;
  showFileNameFormat?: boolean;
  individualPlant?: boolean;
  validateRestrictions?: (
    headerOption: ILocHeaderOption,
    sortOption: IOption,
    company: CompanySummary
  ) => boolean;
}

export const getLabelTypePrintOption = (labelType: EnumLocLabelType) => {
  const options = new Map<EnumLocLabelType, ILabelTypePrintOptions>([
    [
      EnumLocLabelType.InHouse,
      {
        generateLocRequired: (
          selectedPrintOption: IPrintOption,
          productCount: number
        ) => productCount > MaxNumberOfSingleLocsForImmediateDownload,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant],
            text: "Company and Plant Name",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatARestricted || !company.FormatCRestricted,
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatBRestricted,
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name Only",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatDRestricted,
          },
        ],
        sortOrderOptions: [
          { value: "false", text: "Brand => Product" },
          { value: "true", text: "Product => Brand" },
        ],
        printOptions: [
          {
            value: "InHouseCombinedProducts",
            text: "Combined Products",
            validateRestrictions: (
              headerOption: ILocHeaderOption,
              sortOption: IOption,
              company: CompanySummary
            ) => {
              return (
                headerOption.value !==
                  EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant] ||
                !company.FormatARestricted
              );
            },
          },
          {
            value: "SeparateFileForEachProduct",
            text: "Separate File for each Product",
            showFolderName: true,
            showFileNameFormat: true,
            validateRestrictions: (
              headerOption: ILocHeaderOption,
              sortOption: IOption,
              company: CompanySummary
            ) => {
              return (
                sortOption.value === "false" &&
                (headerOption.value !==
                  EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant] ||
                  !company.FormatARestricted)
              );
            },
          },
          {
            value: "InHouseSeparateFileForEachBrand",
            text: "Separate File for each Brand (combined products)",
            showFolderName: true,
            validateRestrictions: (
              headerOption: ILocHeaderOption,
              sortOption: IOption,
              company: CompanySummary
            ) => {
              return (
                sortOption.value === "false" &&
                headerOption.value !==
                  EnumLocHeaderOption[EnumLocHeaderOption.Plant] &&
                (headerOption.value !==
                  EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant] ||
                  !company.FormatARestricted)
              );
            },
          },
          {
            value: "InHouseSeparateFileForEachPlant",
            text: "Separate File for each Plant (combined products)",
            showFolderName: true,
            validateRestrictions: (
              headerOption: ILocHeaderOption,
              sortOption: IOption,
              company: CompanySummary
            ) => {
              return (
                (headerOption.value ===
                  EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant] ||
                  headerOption.value ===
                    EnumLocHeaderOption[EnumLocHeaderOption.Plant]) &&
                sortOption.value === "false"
              );
            },
          },
          {
            value: "InHouseCompanyAndPlantWithPlantSepartedByPage",
            text: "Company and Plant Name - Plant separated by page",
            individualPlant: true,
            validateRestrictions: (
              headerOption: ILocHeaderOption,
              sortOption: IOption,
              company: CompanySummary
            ) => {
              return (
                headerOption.value ===
                  EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant] &&
                sortOption.value === "false"
              );
            },
          },
        ],
        letterHeaderPreference:
          "KosherCertificatesInHouseLetterHeaderPreference",
        sortOrderPreference: "KosherCertificatesInHouseSortOrderPreference",
        printOptionPreference:
          "KosherCertificatesInHousePrintOptionsPreference",
        nameBuilderPreference: "KosherCertificatesInHouseNameBuilderPreference",
      },
    ],
    [
      EnumLocLabelType.PrivateLabel,
      {
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.CompanyPlant],
            text: "Manufacturer, Plant and Private Label Company",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatJRestricted,
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.DistributorOnly],
            text: "Private Label Company",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatLRestricted,
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Manufacturer and Private Label Company",
            validateRestrictions: (company: CompanySummary) =>
              !company.FormatKRestricted,
          },
        ],
        printOptions: [
          {
            value: "PrivateLabelSeparateFileForEachPlCompany",
            text: "Separate File for each Private Label Company",
            showFolderName: true,
          },
          {
            value: "PrivateLabelCombinedFileForAllPlCompanies",
            text: "Combined file separated by page break between PLs",
          },
        ],
        letterHeaderPreference: "KosherCertificatesPlLetterHeaderPreference",
        printOptionPreference: "KosherCertificatesPlPrintOptionsPreference",
      },
    ],
    [
      EnumLocLabelType.Repack,
      {
        generateLocRequired: (
          selectedPrintOption: IPrintOption,
          productCount: number
        ) => productCount > MaxNumberOfSingleLocsForImmediateDownload,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name Only",
          },
        ],
        printOptions: [
          {
            value: "SeparateFileForEachProduct",
            text: "Separate File for each Product",
            showFolderName: true,
            showFileNameFormat: true,
          },
        ],
        letterHeaderPreference:
          "KosherCertificatesRepackLetterHeaderPreference",
        nameBuilderPreference: "KosherCertificatesRepackNameBuilderPreference",
      },
    ],
    [
      EnumLocLabelType.Transport,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name",
          },
        ],
      },
    ],
    [
      EnumLocLabelType.InternalWash,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name",
          },
        ],
      },
    ],
    [
      EnumLocLabelType.UpgradeWash,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name",
          },
        ],
      },
    ],
    [
      EnumLocLabelType.MaintenanceWash,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Plant],
            text: "Plant Name",
          },
        ],
      },
    ],
    [
      EnumLocLabelType.StorageFacility,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
        ],
      },
    ],
    [
      EnumLocLabelType.StorageTanks,
      {
        isLinkOption: true,
        letterHeaderOptions: [
          {
            value: EnumLocHeaderOption[EnumLocHeaderOption.Company],
            text: "Company Name",
          },
        ],
      },
    ],
  ]);
  return options.get(labelType);
};

export const fileNameBuilderOptions = [
  { value: "{Label Name}", text: "Label Name" },
  { value: "{Label Number}", text: "Label Number" },
  { value: "{UKD-ID}", text: "UKD-ID" },
  { value: "{Date/Time}", text: "Date/Time" },
  { value: "{Brand}", text: "Brand" },
  { value: "{DPM}", text: "DPM" },
  { value: "{Group}", text: "Group" },
] as IOption[];
